/**
 * GettingStartedChecklist Component
 *
 * Tracks first-time actions for new users:
 * - First reflection upload
 * - First tracker entry
 * Shown on the dashboard for users with 0 reflections.
 * Dismissible via localStorage flag.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Calendar, ArrowRight, X, CheckCircle } from "lucide-react";

const DISMISS_KEY = "attunewell_checklist_dismissed";

interface GettingStartedChecklistProps {
  hasReflections: boolean;
  hasTrackerEntries: boolean;
}

export function GettingStartedChecklist({
  hasReflections,
  hasTrackerEntries,
}: GettingStartedChecklistProps) {
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(DISMISS_KEY) === "true"
  );

  // Don't show if dismissed or all items completed
  if (dismissed || (hasReflections && hasTrackerEntries)) return null;

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, "true");
    setDismissed(true);
  };

  const items = [
    {
      label: "Upload your first conversation",
      description: "Get AI-powered insights from a recorded conversation",
      done: hasReflections,
      action: () => navigate("/reflections"),
      icon: Upload,
    },
    {
      label: "Try the relationship tracker",
      description: "Log a daily entry about your relationship",
      done: hasTrackerEntries,
      action: () => navigate("/tracker"),
      icon: Calendar,
    },
  ];

  return (
    <div className="relative rounded-lg border bg-card p-6 mb-8">
      <button
        onClick={handleDismiss}
        className="absolute top-3 right-3 text-muted-foreground hover:text-foreground"
        aria-label="Dismiss checklist"
      >
        <X className="w-4 h-4" />
      </button>
      <h3 className="font-semibold text-foreground mb-4">Getting Started</h3>
      <div className="space-y-3">
        {items.map((item) => (
          <button
            key={item.label}
            onClick={item.action}
            disabled={item.done}
            className={`flex items-center gap-3 w-full p-3 rounded-lg text-left transition-colors ${
              item.done
                ? "bg-muted/50 opacity-60"
                : "hover:bg-muted/50 cursor-pointer"
            }`}
          >
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                item.done ? "bg-green-100 dark:bg-green-900" : "bg-primary/10"
              }`}
            >
              {item.done ? (
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
              ) : (
                <item.icon className="w-4 h-4 text-primary" />
              )}
            </div>
            <div className="flex-1">
              <div
                className={`text-sm font-medium ${item.done ? "line-through text-muted-foreground" : "text-foreground"}`}
              >
                {item.label}
              </div>
              <div className="text-xs text-muted-foreground">
                {item.description}
              </div>
            </div>
            {!item.done && (
              <ArrowRight className="w-4 h-4 text-muted-foreground" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
